// src/components/pages/admin/LocationPicker.js
import React, { useRef, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import useGeolocationMap from "../../../hooks/useGeolocationMap";
import LocationSearchBox from "../../LocationSearchBox";
import useLocationSearch from "../../../hooks/useLocationSearch";
import ActionButton from "../../atoms/ActionButton";
import { NAVBAR_HEIGHT } from "../../../constants/constants";

import IconMessageDefault from "../../../assets/icons/message-default.svg";
import IconRecenter from "../../../assets/icons/icon-recenter.svg";
import IconBuryTimeCapsule from "../../../assets/icons/icon-bury-capsule.png";

// Google Maps JS API loader
import { googleMapsLoader } from "../../../googleMaps";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

let placesService = null;
let geocoder = null;
googleMapsLoader.load().then(() => {
  const google = window.google;
  placesService = new google.maps.places.PlacesService(
    document.createElement("div")
  );
  geocoder = new google.maps.Geocoder();
});

const LocationPicker = ({ onLocationSelect, initialLocation }) => {
  const mapContainerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const hasCenteredRef = useRef(false);

  // determine default center & zoom
  const defaultCoords = initialLocation
    ? { lng: initialLocation.lng, lat: initialLocation.lat, zoom: 16 }
    : { lng: -98.5795, lat: 39.8283, zoom: 16 };

  // only allow browser geo on first-open if no initialLocation
  const allowGeo = !initialLocation;

  // initialize map & geolocation
  const { map, coords, geolocationLoaded, handleRecenter } = useGeolocationMap(
    mapContainerRef,
    defaultCoords,
    "mapbox://styles/jaimeboyaca/cm7f1viks00ad01qsa8cy3xdb",
    IconBuryTimeCapsule,
    allowGeo
  );

  // manage search box state
  const { searchQuery, setSearchQuery, suggestions } = useLocationSearch(
    initialLocation?.address || "",
    coords
  );

  // detect when the Mapbox map is fully loaded
  useEffect(() => {
    if (!map) return;
    const onLoad = () => setMapLoaded(true);
    map.on("load", onLoad);
    return () => map.off("load", onLoad);
  }, [map]);

  // initial centering: either use initialLocation or first geolocation coords
  useEffect(() => {
    if (!map || !mapLoaded || hasCenteredRef.current) return;
    if (initialLocation) {
      // center on previously picked location
      map.jumpTo({
        center: [initialLocation.lng, initialLocation.lat],
        zoom: defaultCoords.zoom,
      });
      setSearchQuery(initialLocation.address);
      hasCenteredRef.current = true;
    } else if (geolocationLoaded && coords) {
      // first-time: center on user's current location
      map.jumpTo({
        center: [coords.lng, coords.lat],
        zoom: defaultCoords.zoom,
      });
      // reverse-geocode effect will fill in the search box
      hasCenteredRef.current = true;
    }
  }, [map, mapLoaded, initialLocation, geolocationLoaded, coords, setSearchQuery, defaultCoords.zoom]);

  // reverse-geocode to keep search box in sync with map center
  useEffect(() => {
    if (!coords || !geocoder) return;
    geocoder.geocode(
      { location: { lat: coords.lat, lng: coords.lng } },
      (results, status) => {
        if (status === "OK" && results[0]) {
          setSearchQuery(results[0].formatted_address);
        }
      }
    );
  }, [coords, setSearchQuery]);

  // fly to selected place from suggestions
  const handleSelect = (prediction) => {
    if (!placesService || !map) return;
    placesService.getDetails(
      { placeId: prediction.place_id },
      (place, status) => {
        if (status === "OK" && place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          map.flyTo({ center: [lng, lat], zoom: 16 });
          setSearchQuery(prediction.description);
        }
      }
    );
  };

  // confirm & hand back to parent
  const handleConfirmLocation = () => {
    if (!coords || !searchQuery) return;
    onLocationSelect({
      lat: coords.lat,
      lng: coords.lng,
      address: searchQuery,
    });
  };

  // show loader until geolocation (if needed) is ready
  if (!geolocationLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "400px" }}>
      {/* Autocomplete / Search Box */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
          zIndex: 2,
          bgcolor: "white",
          borderRadius: "50px",
        }}
      >
        <LocationSearchBox
          searchQuery={searchQuery}
          suggestions={suggestions}
          onInputChange={(e, v) => setSearchQuery(v)}
          onSelect={handleSelect}
          textFieldProps={{
            placeholder: "Search or type an address",
            variant: "outlined",
            size: "small",
            sx: {
              "& .MuiOutlinedInput-root": {
                borderRadius: "50px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.blue,
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 45px 8px 42px !important",
              },
            },
          }}
        />
      </Box>

      {/* Map Container */}
      <Box
        ref={mapContainerRef}
        sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
      />

      {/* Center Marker Icon */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
        }}
      >
        <img
          src={IconBuryTimeCapsule}
          alt="Drop Location"
          style={{ width: 82, height: 46 }}
        />
      </Box>

      {/* Confirm Location Button */}
      <Box
        sx={{
          position: "absolute",
          bottom: `20px`,
          left: 16,
          zIndex: 2,
        }}
      >
        <ActionButton
          icon={IconMessageDefault}
          text="Confirm Location"
          onClick={handleConfirmLocation}
          sx={(theme) => ({
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
            pl: "10px",
            pt: "10px",
            pb: "10px",
            boxShadow: "none",
            textTransform: "none",
            flex: "none",
          })}
        />
      </Box>

      {/* Recenter Button */}
      <Box
        sx={{
          position: "absolute",
          bottom: `20px`,
          right: 16,
          zIndex: 2,
        }}
      >
        <ActionButton
          icon={IconRecenter}
          onClick={handleRecenter}
          sx={(theme) => ({
            flex: "none",
            width: 50,
            height: 50,
            minWidth: "50px",
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          })}
        />
      </Box>
    </Box>
  );
};

export default LocationPicker;
